import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { get, ref, set, update } from 'firebase/database';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { database } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  canonicalInterviewGoals,
  interviewGoalHash,
  interviewLanguageMode,
  requiresConversationalRegeneration
} from './liveInterviewQuestionUtils';
import './AssessmentEditor.css';
import EditorSettings from './EditorSettings';
import './LiveAiInterviewEditor.css';

const MAX_GOALS = 6;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || process.env.REACT_APP_BACKEND_URL || '';

// Preserve the editor's public helper exports for existing imports and tests.
export {
  canonicalInterviewGoals,
  interviewGoalHash,
  interviewLanguageMode,
  requiresConversationalRegeneration
} from './liveInterviewQuestionUtils';

/** Resolve the owner company for both owners and existing collaborators. */
const resolveCompanyId = async (userId) => {
  const owner = await get(ref(database, `companies/${userId}`));
  if (owner.exists()) return userId;
  const collaborator = await get(ref(database, `collaborators/${userId}`));
  return collaborator.exists() ? collaborator.val()?.companyId || '' : '';
};

/** Dedicated employer configuration screen for the live conversational product. */
const LiveAiInterviewEditor = () => {
  useUiLanguage();
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isRTL } = useLanguage();
  const { t } = useTranslation('applicants');
  const requestedJobId = searchParams.get('job') || '';
  const pendingApplicantId = searchParams.get('applicant') || '';
  const pendingConnectionKey = searchParams.get('connection') || '';

  const [companyId, setCompanyId] = useState('');
  const [record, setRecord] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(requestedJobId);
  const [draft, setDraft] = useState({ title: '', duration_minutes: 20, languageMode: 'en', assessment_prompt: '', grading_rubric: '', questions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser) return;
    resolveCompanyId(currentUser.uid).then(setCompanyId).catch(() => setError(t('liveInterviewEditor.accessError')));
  }, [currentUser, t]);

  useEffect(() => {
    if (!companyId) return;
    let active = true;
    const load = async () => {
      try {
        const snapshot = await get(ref(database, `companies/${companyId}/assessments/${assessmentId}`));
        if (!snapshot.exists()) throw new Error(t('liveInterviewEditor.notFound'));
        const value = snapshot.val();
        if (value?.deliveryType !== 'two-way-voice' || value?.answerFormat !== 'live-voice') {
          navigate(`/assessment-editor/${assessmentId}`, { replace: true });
          return;
        }
        if (!active) return;
        const jobs = value.jobQuestions || {};
        const jobId = (requestedJobId && jobs[requestedJobId]) ? requestedJobId : (Object.keys(jobs)[0] || '');
        setRecord(value);
        setSelectedJobId(jobId);
        let source = jobs[jobId] || value.previewAssessment || value;
        if (
          jobId
          && source?.status !== 'approved'
          && requiresConversationalRegeneration(source?.questions)
        ) {
          const authToken = await currentUser.getIdToken();
          const response = await fetch(
            `${BACKEND_URL}/companies/${encodeURIComponent(companyId)}/ai-interviews/${encodeURIComponent(assessmentId)}/jobs/${encodeURIComponent(jobId)}/coverage-goals`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
              body: JSON.stringify({ count: Math.min(MAX_GOALS, Math.max(1, Number(value.baseConfig?.numQuestions) || 5)) })
            }
          );
          const payload = await response.json();
          if (!response.ok || !payload?.draft?.questions?.length) {
            throw new Error(payload?.detail || t('liveInterviewEditor.questionUpdateError'));
          }
          source = payload.draft;
          value.jobQuestions = { ...jobs, [jobId]: source };
          setNotice(t('liveInterviewEditor.questionsUpdated'));
        }
        setDraft({
          title: value.title || 'Two-way AI interview',
          duration_minutes: Math.min(20, Math.max(1, Number(source.duration_minutes || value.duration_minutes || 20))),
          languageMode: interviewLanguageMode(value),
          assessment_prompt: value.assessment_prompt || value.baseConfig?.assessmentPrompt || '',
          grading_rubric: value.grading_rubric || value.baseConfig?.gradingRubric || '',
          questions: canonicalInterviewGoals(source.questions || [])
        });
        setDirty(false);
      } catch (loadError) {
        if (active) setError(loadError.message || t('liveInterviewEditor.loadError'));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [assessmentId, companyId, currentUser, navigate, requestedJobId, t]);

  const jobs = useMemo(() => Object.entries(record?.jobQuestions || {}).map(([id, value]) => ({
    id,
    title: value.jobTitle || id,
    department: value.jobDepartment || ''
  })), [record]);
  const selectedRecord = selectedJobId ? record?.jobQuestions?.[selectedJobId] : null;
  const approved = Boolean(!dirty && selectedRecord?.status === 'approved' && selectedRecord?.approvedAt && selectedRecord?.contentHash);

  /** Switch the working copy to a job's independently approved coverage goals. */
  const chooseJob = (jobId) => {
    const value = record.jobQuestions[jobId];
    setSelectedJobId(jobId);
    setDraft((current) => ({
      ...current,
      duration_minutes: Math.min(20, Math.max(1, Number(value.duration_minutes || record.duration_minutes || 20))),
      questions: canonicalInterviewGoals(value.questions || [])
    }));
    setNotice('');
    setError('');
    setDirty(false);
    navigate(`/interview-editor/${assessmentId}?job=${jobId}`, { replace: true });
  };

  /** Update one coverage goal without introducing assessment answer formats. */
  const updateGoal = (index, text) => {
    setDirty(true);
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((goal, goalIndex) => goalIndex === index ? { ...goal, text } : goal)
    }));
  };

  /** Add one employer-owned conversational coverage goal, capped at six. */
  const addGoal = () => {
    setDirty(true);
    setDraft((current) => current.questions.length >= MAX_GOALS ? current : ({
      ...current,
      questions: [...current.questions, { id: `goal-${Date.now()}`, text: '' }]
    }));
  };

  /** Save and explicitly approve the exact job-bound goal set. */
  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const questions = canonicalInterviewGoals(draft.questions);
      if (!questions.length) throw new Error(t('liveInterviewEditor.addQuestionError'));
      const duration = Math.min(20, Math.max(1, Number(draft.duration_minutes) || 20));
      const interviewLanguages = draft.languageMode === 'candidate-choice' ? ['en', 'ar'] : [draft.languageMode];
      const root = `companies/${companyId}/assessments/${assessmentId}`;
      await update(ref(database, root), {
        title: draft.title.trim() || 'Two-way AI interview',
        duration_minutes: duration,
        interviewLanguages,
        defaultInterviewLanguage: draft.languageMode === 'ar' ? 'ar' : 'en',
        assessment_prompt: draft.assessment_prompt || '',
        grading_rubric: draft.grading_rubric || ''
      });
      if (selectedJobId) {
        const existing = record.jobQuestions[selectedJobId] || {};
        const approval = {
          ...existing,
          questions,
          duration_minutes: duration,
          status: 'approved',
          approvedAt: new Date().toISOString(),
          approvedBy: currentUser.uid,
          contentHash: await interviewGoalHash(questions)
        };
        await set(ref(database, `${root}/jobQuestions/${selectedJobId}`), approval);
        setRecord((current) => ({ ...current, title: draft.title, duration_minutes: duration, jobQuestions: { ...current.jobQuestions, [selectedJobId]: approval } }));
        setDirty(false);
        if (pendingApplicantId && pendingConnectionKey) {
          const authToken = await currentUser.getIdToken();
          const response = await fetch(`${BACKEND_URL}/ai-interviews/invitations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ companyId, connectionKey: pendingConnectionKey, assessmentId })
          });
          const invitation = await response.json();
          if (!response.ok || !invitation?.candidateUrl) {
            throw new Error(`${t('liveInterviewEditor.savedInviteFailed')} ${invitation?.detail || invitation?.error || t('liveInterviewEditor.unknownError')}`);
          }
          if (!invitation?.delivery?.complete) {
            throw new Error(t('liveInterviewEditor.someInvitesFailed'));
          }
          setNotice(t('liveInterviewEditor.savedInviteSent'));
          navigate(`/interview-editor/${assessmentId}?job=${encodeURIComponent(selectedJobId)}`, { replace: true });
        } else {
          const authToken = await currentUser.getIdToken();
          const response = await fetch(
            `${BACKEND_URL}/companies/${encodeURIComponent(companyId)}/ai-interviews/${encodeURIComponent(assessmentId)}/jobs/${encodeURIComponent(selectedJobId)}/pending-invitations`,
            { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } }
          );
          const delivery = await response.json();
          if (!response.ok || delivery?.failed?.length) {
            throw new Error(t('liveInterviewEditor.pendingInvitesFailed', { count: delivery?.failed?.length || 1 }));
          }
          setNotice(delivery?.delivered
            ? t('liveInterviewEditor.pendingInvitesSent', { count: delivery.delivered })
            : t('liveInterviewEditor.savedNoInvites'));
        }
      } else {
        const previewAssessment = {
          ...(record.previewAssessment || {}),
          questions,
          duration_minutes: duration,
          generatedAt: new Date().toISOString()
        };
        await update(ref(database, root), { previewAssessment });
        setRecord((current) => ({ ...current, previewAssessment }));
        setNotice(t('liveInterviewEditor.templateSaved'));
      }
      setDirty(false);
    } catch (saveError) {
      setError(saveError.message || t('liveInterviewEditor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ae-loading"><i className="fa-solid fa-spinner fa-spin" /><p>{t('liveInterviewEditor.loading')}</p></div>;
  if (error && !record) return <div className="ae-loading live-editor-error">{uiText(error)}</div>;

  return (
    <main className="ae-shell live-editor-shell" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="ae-back-row">
        <button className="ae-back-btn" onClick={() => navigate('/applicants')}>
          <i className={`fa-solid ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'}`} /> {t('liveInterviewEditor.back')}
        </button>
      </div>

      <div className="ae-content live-editor-content">
        <header className="ae-header">
          <div className="ae-header-title">
            <h2><i className="fa-solid fa-robot" /> {t('liveInterviewEditor.title')}</h2>
            <p className="ae-inline-note">{t('liveInterviewEditor.subtitle')}</p>
          </div>
          <div className="ae-header-actions">
            <button className="ae-btn-primary" onClick={save} disabled={saving}>
              <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-shield-check'}`} />
              {saving ? t('liveInterviewEditor.saving') : selectedJobId ? t('liveInterviewEditor.saveForJob') : t('liveInterviewEditor.saveTemplate')}
            </button>
            <EditorSettings title={t('assessmentEditor.interviewSettings')} t={t}>
              <section className="ae-meta-section live-editor-overview">
                <div>
                  <label className="ae-field-label">{t('liveInterviewEditor.name')}</label>
                  <input className="ae-input" value={draft.title} onChange={(event) => { setDirty(true); setDraft((current) => ({ ...current, title: event.target.value })); }} />
                </div>
                <div>
                  <label className="ae-field-label">{t('liveInterviewEditor.duration')}</label>
                  <div className="live-editor-duration"><input className="ae-input ae-input-short" type="number" min="1" max="20" value={draft.duration_minutes} onChange={(event) => { setDirty(true); setDraft((current) => ({ ...current, duration_minutes: event.target.value })); }} /><span>{t('liveInterviewEditor.minutes')}</span></div>
                </div>
                <div>
                  <label className="ae-field-label">{t('liveInterviewEditor.language')}</label>
                  <select className="ae-select live-editor-full-field" value={draft.languageMode} onChange={(event) => { setDirty(true); setDraft((current) => ({ ...current, languageMode: event.target.value })); }}>
                    <option value="candidate-choice">{t('liveInterviewEditor.candidateChoice')}</option>
                    <option value="en">{t('liveInterviewEditor.english')}</option>
                    <option value="ar">{t('liveInterviewEditor.arabic')}</option>
                  </select>
                  <p className="ae-inline-note">{t('liveInterviewEditor.languageHelp')}</p>
                </div>
              </section>
              <section className="ae-meta-section live-editor-guidance">
                <div><label className="ae-field-label">{t('liveInterviewEditor.notes')}</label><p className="ae-inline-note">{t('liveInterviewEditor.notesHelp')}</p><textarea className="ae-textarea live-editor-guidance-textarea" rows="4" value={draft.assessment_prompt} onChange={(event) => { setDirty(true); setDraft((current) => ({ ...current, assessment_prompt: event.target.value })); }} placeholder={t('liveInterviewEditor.notesPlaceholder')} /></div>
                <div><label className="ae-field-label"><i className="fa-solid fa-lock" /> {t('liveInterviewEditor.reviewRules')}</label><p className="ae-inline-note">{t('liveInterviewEditor.reviewRulesHelp')}</p><textarea className="ae-textarea live-editor-guidance-textarea" rows="5" value={draft.grading_rubric} onChange={(event) => { setDirty(true); setDraft((current) => ({ ...current, grading_rubric: event.target.value })); }} placeholder={t('liveInterviewEditor.reviewRulesPlaceholder')} /></div>
              </section>
            </EditorSettings>
          </div>
        </header>

        {(notice || error) && <div className={`live-editor-notice ${error ? 'is-error' : ''}`} role="status">{error || notice}</div>}

        <section className="ae-meta-section live-editor-overview">
          {jobs.length > 0 && <div>
            <label className="ae-field-label">{t('liveInterviewEditor.job')}</label>
            <select className="ae-select live-editor-full-field" value={selectedJobId} onChange={(event) => chooseJob(event.target.value)}>
              {jobs.map((job) => <option key={job.id} value={job.id}>{job.department ? `${job.title} — ${job.department}` : job.title}</option>)}
            </select>
          </div>}
          {jobs.length === 0 && <div>
            <label className="ae-field-label">{t('liveInterviewEditor.job')}</label>
            <div className="ae-preview-notice">
              <i className="fa-solid fa-circle-info" />
              <div><strong>{t('liveInterviewEditor.noJobDraft')}</strong><span>{t('liveInterviewEditor.noJobDraftHelp')}</span></div>
            </div>
          </div>}
          <div className={`ae-preview-notice live-editor-status ${approved ? 'is-approved' : ''}`}>
            <i className={`fa-solid ${approved ? 'fa-circle-check' : 'fa-clock'}`} />
            <div><strong>{approved ? t('liveInterviewEditor.ready') : selectedJobId ? t('liveInterviewEditor.reviewNeeded') : t('liveInterviewEditor.jobNeeded')}</strong><span>{approved ? t('liveInterviewEditor.readyHelp') : t('liveInterviewEditor.reviewHelp')}</span></div>
          </div>
        </section>

        <section className="live-editor-section">
          <div className="ae-section-header"><div><h3 className="ae-section-title">{t('liveInterviewEditor.mainQuestions')}</h3><p className="ae-inline-note">{t('liveInterviewEditor.mainQuestionsHelp')}</p></div><span className="ae-count-badge">{draft.questions.length}/{MAX_GOALS}</span></div>
          <div className="ae-question-list">
            {draft.questions.map((goal, index) => <div className="ae-question-row" key={goal.id}>
              <div className="ae-question-head">
                <span aria-hidden="true" />
                <div className="ae-question-meta"><span className="ae-question-num">{t('liveInterviewEditor.questionNumber', { number: index + 1 })}</span><span className="ae-question-type-badge">{t('liveInterviewEditor.liveInterview')}</span></div>
                <div className="ae-question-actions"><button type="button" className="ae-delete-btn" aria-label={t('liveInterviewEditor.removeQuestion', { number: index + 1 })} onClick={() => { setDirty(true); setDraft((current) => ({ ...current, questions: current.questions.filter((_, itemIndex) => itemIndex !== index) })); }}><i className="fa-solid fa-trash" /></button></div>
              </div>
              <div className="ae-question-body"><textarea className="ae-textarea" rows="3" value={goal.text} onChange={(event) => updateGoal(index, event.target.value)} placeholder={t('liveInterviewEditor.questionPlaceholder')} /></div>
            </div>)}
            {!draft.questions.length && <div className="ae-empty-state"><i className="fa-solid fa-comments" />{t('liveInterviewEditor.noQuestions')}</div>}
          </div>
          <div className="ae-presets"><button className="ae-preset-btn" onClick={addGoal} disabled={draft.questions.length >= MAX_GOALS}><i className="fa-solid fa-plus" /> {t('liveInterviewEditor.addQuestion')}</button></div>
        </section>
      </div>
    </main>
  );
};

export default LiveAiInterviewEditor;
